/** Web Push：讓瀏覽器在分頁關掉後也能收到小哨的通知。
 *
 *  - 推播需要 https（127.0.0.1 與 localhost 例外）。
 *  - iPhone、iPad 要先「加入主畫面」，從主畫面開啟後才有推播。
 *  - 推播服務不保證送達；站內通知中心才是完整紀錄。
 */
import { apiGet, apiPost } from '@/lib/api';
import type { PushStatus } from '@/types/assistant';

export type PushSupport = 'supported' | 'insecure' | 'needs-install' | 'unsupported';

const ENABLED_KEY = (account: string) => `edgeai:push:v1:${account}`;

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return 'insecure';
  if (isIOS() && !isStandalone()) return 'needs-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return 'supported';
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

let registering: Promise<ServiceWorkerRegistration> | null = null;

function registerWorker() {
  registering ??= navigator.serviceWorker.register('/sw.js', { scope: '/' });
  return registering;
}

function keyBytes(base64url: string) {
  const padded = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

function sameKey(current: ArrayBuffer | null, base64url: string) {
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = keyBytes(base64url);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export async function currentSubscription() {
  if (pushSupport() !== 'supported') return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

async function bind(sub: PushSubscription) {
  const json = sub.toJSON();
  await apiPost('/api/v1/push/subscriptions', { endpoint: json.endpoint, keys: json.keys });
}

/** 使用者按下「開啟推播」：要權限、訂閱，再綁到目前登入的帳號。 */
export async function enablePush(account: string) {
  const permission = await Notification.requestPermission();
  if (permission === 'denied') throw new Error('瀏覽器封鎖了這個網站的通知，請到網址列左側的網站設定改成允許');
  if (permission !== 'granted') throw new Error('沒有取得通知權限，請再按一次並選擇允許');
  const reg = await registerWorker();
  await navigator.serviceWorker.ready;
  const { public_key: publicKey } = await apiGet<{ public_key: string }>('/api/v1/push/config');
  let sub = await reg.pushManager.getSubscription();
  // 伺服器換過金鑰時，舊的訂閱收不到，要先退掉再重訂
  if (sub && !sameKey(sub.options.applicationServerKey, publicKey)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await bind(sub);
  try { localStorage.setItem(ENABLED_KEY(account), 'on'); } catch { /* 無法保存時，下次登入再手動開啟。 */ }
}

/** 只關這台瀏覽器；其他裝置上的推播不受影響。 */
export async function disablePush(account: string) {
  try { localStorage.removeItem(ENABLED_KEY(account)); } catch { /* 略過 */ }
  const sub = await currentSubscription();
  if (!sub) return;
  await apiPost('/api/v1/push/subscriptions/remove', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

export async function pushStatus() {
  const sub = await currentSubscription();
  const status = sub ? await apiPost<PushStatus>('/api/v1/push/status', { endpoint: sub.endpoint }) : null;
  return { subscribed: Boolean(sub), status };
}

/** 登入後：這個帳號之前在這台瀏覽器開過推播，就默默重新綁定（登出時伺服器端已撤銷）。 */
export async function restorePush(account: string) {
  try {
    if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return;
    if (localStorage.getItem(ENABLED_KEY(account)) !== 'on') return;
    await registerWorker();
    const sub = await currentSubscription();
    if (sub) await bind(sub);
  } catch { /* 綁不上就維持關閉，通知中心會顯示狀態。 */ }
}

/** 登出前：先撤銷伺服器上的綁定，這台瀏覽器之後就收不到這個帳號的推播。 */
export async function releasePush() {
  try {
    const sub = await currentSubscription();
    if (sub) await apiPost('/api/v1/push/subscriptions/remove', { endpoint: sub.endpoint });
  } catch { /* 登出不該被推播卡住。 */ }
}
